import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { rem } from '@utils';

const Wrapper = styled.div<{ viewportHeight: number }>`
  display: flex;
  flex-direction: column;

  // To extract the height of an empty chat area
  &:first-of-type:last-of-type {
    flex: 1;
  }

  &.prompt-echo {
    padding: ${rem(32)} 0;
  }

  &:last-of-type {
    padding-bottom: ${rem(32)};
    min-height: ${({ viewportHeight }) => viewportHeight}px;
  }
`;

const Base = styled.div(
  ({ theme }) => `
  color: var(--chat-message-color);
  max-height: max-content;
  position: relative;

  > *:not(${SeeMoreButton}) {
    color: ${theme.palette.common.black};
    font-size: ${rem(16)};
    font-weight: 400;
    line-height: ${rem(24)};
  }

  b, strong {
    font-weight: 600;
  }

  // Overwrite external typography
  h1, h2, h3, h4, h5, h6 {
    color: ${theme.palette.common.black};
    text-transform: none;
  }  

  h1, h2 {
    font-size: ${rem(24)};
    font-weight: 700;
    line-height: ${rem(32)};
    margin-bottom: ${rem(24)};
  }

  h3, h4 {
    font-size: ${rem(20)};
    font-weight: 600;
    line-height: ${rem(32)};
    margin-bottom: 0;
  }

  h5, h6 {
    font-size: ${rem(16)};
    font-weight: 500;
    line-height: ${rem(24)};
    margin-bottom: 0;
  }

  *:is(h3, h4, h5, h6):not(:last-child) {
    margin-bottom: ${rem(16)};
  }  

  * + *:is(h1, h2, h3, h4, h5, h6) {
    margin-top: ${rem(24)};
  }

  *:is(h1, h2, h3, h4, h5, h6) + *:is(ul, ol) {
    margin-top: -${rem(8)};
  }

  blockquote {
    font-style: italic;
    padding: ${rem(8)} ${rem(12)};
    position: relative;

    &::before {
      content: '';
      background: ${theme.palette.grey['300']};
      border-radius: 4px;
      position: absolute;
      left: 0;
      top: 0;
      width: ${rem(4)};
      height: 100%;
    }

    > p::before {
      content: '“';
      margin-right: ${rem(2)};
      font-family: sans-serif;
    }

    > p::after {
      content: '”';
      font-family: sans-serif;
    }
  }

  hr {
    background: ${theme.palette.grey['200']};
    border: none;
    height: 1px;
    margin: ${rem(32)} 0;

    @media (max-width: 430px) {
      margin: ${rem(24)} 0;
    }
  }

  *:is(ul, ol):not(:last-child) {
    margin-bottom: ${rem(8)};
  }

  li {
    margin-left: ${rem(32)};
    padding-top: ${rem(8)};
    padding-bottom: ${rem(8)};
  }

  p:not(:last-child) {
    margin-bottom: ${rem(8)};
  }

  p + p {
    margin-top: ${rem(16)};
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: ${rem(32)};

    th, td {
      font-size: ${rem(14)};
      line-height: ${rem(16)};
      padding: ${rem(16)} ${rem(8)};
      text-align: left;
      text-wrap: balance;
    }
     
    th {
      border-bottom: 1px solid ${theme.palette.grey['400']};
      font-weight: 600;
    }

    td {
      border-bottom: 1px solid ${theme.palette.grey['300']};
    }

    & + *:is(h1, h2, hr) {
      margin-top: ${rem(48)};
    }
  }
`
);

const shimmer = keyframes`
  0% {
    background-position: 100% 0;
  }

  100% {
    background-position: -100% 0;
  }
`;

const AgentMessage = styled(Base)(
  ({ theme }) => css`
  overflow-x: hidden;
  width: 100%;

  ${Wrapper}.transient & {
    border: 1px solid ${theme.palette.grey['200']};
    border-radius: 12px;
    min-height: ${rem(70)};
    padding: ${rem(24)};

    *:has(.audience) {
      display: flex;
      flex-wrap: wrap;
      gap: ${rem(8)};
    }

    *:not(h1, h2, h3, h4, h5, h6) {
      color: ${theme.palette.grey['600']};
      font-size: ${rem(14)};
      line-height: ${rem(20)};
    }

    li:last-of-type {
      padding-bottom: 0;
    }

    h1, h2, h3, h4, h5, h6 {
      color: ${theme.palette.grey['600']};
      font-size: ${rem(16)};
      font-weight: 600;
      line-height: ${rem(24)};    
      max-width: max-content;
      position: relative;

      // Shimmer effect
      &:first-of-type::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: linear-gradient(
          -45deg, 
          transparent 0%,
          ${theme.palette.common.white}AA 10%, 
          transparent 30%
        );
        background-size: 200% 100%;
        animation: ${shimmer} 1.5s infinite linear;
      }
    }
  }
`
);

const EchoWrapper = styled.div`
  height: max-content;
  margin-left: auto;
  padding: ${rem(32)} 0;
  width: 100%;
`;

const Echo = styled(Base)<{ expanded?: boolean }>(
  ({ expanded }) => `
  background: var(--chat-message-echo-background-color);
  border-radius: 16px;
  cursor: pointer;
  margin-left: auto;
  max-width: calc(var(--chat-message-max-width) / var(--stage-width) * 100%);
  max-height: ${expanded ? 'none' : 'var(--chat-message-large-max-height)'};
  overflow: hidden;
  padding: ${rem(16)} ${rem(24)} ${rem(typeof expanded === 'boolean' ? 56 : 16)};
  user-select: all;
  width: fit-content;

  @-moz-document url-prefix() {
    max-width: var(--chat-message-max-width);

    @media (max-width: 768px) {
      max-width: 70%;
    }
  } 
  
  --echo-linear-gradient: linear-gradient(
    180deg, 
    rgba(247, 250, 255, 0.00) 0%, 
    rgba(247, 250, 255, 0.40) 8.17%, 
    var(--chat-message-echo-background-color) 100%
  );

  ${SeeMoreButton}::before {
    background: ${expanded ? 'none' : 'var(--echo-linear-gradient)'};
  }

  @media (max-width: 430px) {
    max-width: 70%;
    max-height: ${expanded ? 'none' : 'var(--chat-message-small-max-height)'};
    padding: ${rem(12)} ${rem(16)} ${rem(typeof expanded === 'boolean' ? 48 : 12)};
    
    --echo-linear-gradient: linear-gradient(
      180deg, 
      rgba(247, 250, 255, 0.00) 2%, 
      rgba(247, 250, 255, 0.40) 8.52%, 
      var(--chat-message-echo-background-color) 80%
    );
  }
`
);

const SeeMoreButton = styled.button(
  ({ theme }) => `
  background: inherit;
  border: none;
  color: ${theme.palette.common.black};
  cursor: pointer;
  font-family: inherit;
  font-size: ${rem(14)};
  font-weight: 500;
  line-height: ${rem(16)};
  padding: ${rem(16)} ${rem(24)} ${rem(24)};
  position: absolute;
  inset: auto 0 0;
  text-align: left;
  user-select: none;

  &:hover {
    color: ${theme.palette.primary.main};
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    bottom: calc(100% - 1px);
    width: 100%;
    height: ${rem(48)};
  }

  @media (max-width: 430px) {
    padding: ${rem(8)} ${rem(16)} ${rem(20)};
  }
`
);

export default {
  Wrapper,
  AgentMessage,
  EchoWrapper,
  Echo,
  SeeMoreButton
};
